import type { Omittable } from "@salestrak/ui";
import type { BulkIssueResultFieldsFragment } from "#app/graphql/generated/graphql";
// ---------------------------------------------------------------------------
// Mutation results
// ---------------------------------------------------------------------------
//
// About forty mutations return a union of one success member and one to three
// error members. The rest do not, and no wrapper is invented for them
// (read the payload types in schema.gql):
//
//   bare non-null type   createWorkspace, updateWorkspace, createNote
//   bare nullable type   updateNote, deleteNote, reorderStage,
//                        setDefaultWorkflow, addWidget, ...
//   bare ID              deleteAutomation
//   Boolean!             EVERY sharing and permission mutation, plus
//                        bulkRemoveIssueAssignees and bulkSetPrimaryAssignee
//   array of entities    bulkMoveIssuesToStage, bulkAddIssueAssignees,
//                        moveWidgets
//
// A `Boolean!` mutation has NO error surface. `unwrapResult` is for the unions
// only; the bare ones stay bare and their failures arrive as GraphQL errors
// through the Apollo error link.

/** The three implementors of the schema's one interface, `Error`. */
export const API_ERROR_TYPENAMES = [
  "ValidationError",
  "NotFoundError",
  "PermissionDenied",
] as const;

export type ApiErrorTypename = (typeof API_ERROR_TYPENAMES)[number];

/**
 * The error members, merged. Each carries `code` and `message` from the
 * interface plus its own field: `ValidationError.field`,
 * `NotFoundError.resourceId`/`resourceType`,
 * `PermissionDenied.requiredPermission`.
 *
 * `code` is an OPEN set. `NOT_FOUND`, `PERMISSION_DENIED`,
 * `UNSUPPORTED_CONTENT_TYPE` and `UNSUPPORTED_MEDIA_KIND` are the values seen
 * in the server source; do not switch on it exhaustively.
 */
export type ApiError = Omittable<{
  __typename: ApiErrorTypename;
  code: string;
  message: string;
  field?: string | null;
  resourceId?: string;
  resourceType?: string;
  requiredPermission?: string;
}>;

type ApiErrorMember = { __typename?: ApiErrorTypename };

/** The success member of a result union — every member that is not an error. */
export type SuccessOf<T> = Exclude<T, ApiErrorMember>;

export type UnwrappedResult<T> =
  | { ok: true; data: SuccessOf<T> }
  | { ok: false; error: ApiError | null };

const ERROR_TYPENAMES = new Set<string>(API_ERROR_TYPENAMES);

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    ERROR_TYPENAMES.has((value as { __typename?: string }).__typename ?? "")
  );
}

/**
 * Splits a result union into its success member or its error member.
 *
 * A null/undefined result is `{ ok: false, error: null }` — the mutation
 * resolved to nothing, which several nullable-result mutations can do without
 * returning an error member.
 */
export function unwrapResult<T extends { __typename?: string | null }>(
  result: T | null | undefined,
): UnwrappedResult<T> {
  if (result == null) return { ok: false, error: null };
  if (isApiError(result)) return { ok: false, error: result };
  return { ok: true, data: result as SuccessOf<T> };
}

/** The success member, or undefined. For callers that ignore the error. */
export function resultData<T extends { __typename?: string | null }>(
  result: T | null | undefined,
): SuccessOf<T> | undefined {
  const unwrapped = unwrapResult(result);
  return unwrapped.ok ? unwrapped.data : undefined;
}

/**
 * The message to show for a failed result. `fallback` is REQUIRED: a null
 * result carries no message, and the app's copy rule is
 * "Failed to <verb> <noun>".
 */
export function resultErrorMessage(error: ApiError | null, fallback: string): string {
  return error?.message?.trim() ? error.message : fallback;
}

/**
 * The bulk issue mutations (`bulkUpdateIssues`, `bulkArchiveIssues`,
 * `bulkUnarchiveIssues`) answer per issue: the ones they changed, and each
 * failure with its reason. A batch with any failure throws the count and the
 * first reason, so the bulk bar shows one error toast for it.
 */
export function requireBulkSuccess(result: BulkIssueResultFieldsFragment | undefined): void {
  if (!result) throw new Error("No response");
  const failed = result.failures.length;
  if (failed === 0) return;
  const total = failed + result.succeededIds.length;
  throw new Error(`${failed} of ${total} failed: ${result.failures[0]!.message}`);
}
